import * as Mutations from './mutations';
import { LoginService } from '../../../common/services/login.service';
import { UserData } from '../../../common/auth/user-data';
import router from '../../../router';
import * as Routes from '../../../router/routes';
import { SignUpService } from '../../../common/services/sign-up.service';
import { UsersInvitationService } from '../../../common/services/settings/user-invitation.service';
import { removeSpaces } from '../../../common/utils/strings';
import * as Roles from '../../../constants/user-types';
import * as AbilityMutations from '../access-list/mutations';
import { Confirmation } from '../confirm/Confirmation';
import { UsersService } from '@/common/services/settings/users.service';
import * as RootMutations from '@/store/root-mutations';

export const LoginModule = {
    state: {
        userData: new UserData(),
        userExists: null,
        schools: [],
        isSwitchingAccount: false
    },

    mutations: {
        [Mutations.SET_USER_DATA](state, userData) {
            state.userData = userData;
        },

        [Mutations.SET_USER_EXISTS](state, payload) {
            state.userExists = payload;
        },

        [Mutations.LOG_OUT](state) {
            state.userData = null;
        },

        [Mutations.SET_CURRENT_USER_SCHOOLS](state, schools) {
            state.schools = schools;
        },

        [Mutations.SWITCH_ACCOUNT_REQUEST](state) {
            state.isSwitchingAccount = true;
        },

        [Mutations.SWITCH_ACCOUNT_SUCCESS](state) {
            state.isSwitchingAccount = false;
        },

        [Mutations.SWITCH_ACCOUNT_FAILED](state) {
            state.isSwitchingAccount = false;
        }
    },

    actions: {
        async login(context, form) {
            try {
                context.dispatch('showSpinner');
                const { data } = await LoginService.login(form);
                context.dispatch('saveUserInfo', data);
                context.dispatch('getSchoolInfo');
                const { userRole } = context.getters;
                navigateToStartPage(userRole);
            } finally {
                context.dispatch('saveUsername', form.userName);
                context.dispatch('hideSpinner');
            }
        },

        async adminLogin(context, payload) {
            try {
                const { data } = await LoginService.adminLogin(payload);
                await context.dispatch('saveUserInfo', data);
                const { userRole } = context.getters;
                navigateToStartPage(userRole);
            } catch (e) {
                router.push({ name: Routes.LOGIN });
            }
        },

        async logout(context) {
            await LoginService.logout();
            window.Beacon('destroy');
            UserData.removeUserDataFromLocalStorage();
            context.commit(RootMutations.RESET_STATE);
            context.commit(AbilityMutations.SET_ABILITY, null);
            router.push({ name: Routes.LOGIN });
        },

        async confirmEmail(context, verificationToken) {
            const { data } = await SignUpService.verifyEmail({ verificationToken });
            context.dispatch('saveUserInfo', data);
        },

        async confirmInvitation(context, payload) {
            try {
                const form = { ...payload.form, phone: removeSpaces(payload.form.phone) };
                const { data } = await UsersInvitationService.confirmInvitation(form, payload.token);
                context.commit(RootMutations.RESET_STATE);
                context.commit(AbilityMutations.SET_ABILITY, null);
                context.dispatch('saveUserInfo', data);
                const { userRole } = context.getters;
                navigateToStartPage(userRole);
            } catch (e) {
                router.push({ name: Routes.LOGIN });
                context.dispatch('createErrorNotification', 'Your token is invalid');
            }
        },

        async confirmInvitationIfUserExists(context, token) {
            try {
                context.dispatch('showSpinner');
                const { data } = await UsersInvitationService.checkIfUserExists(token);
                context.commit(Mutations.SET_USER_EXISTS, data);
                if (data) {
                    const { data } = await UsersInvitationService.createAccountForExistingUser(token);
                    context.commit(RootMutations.RESET_STATE);
                    context.commit(AbilityMutations.SET_ABILITY, null);
                    context.dispatch('saveUserInfo', data);
                    const { userRole } = context.getters;
                    navigateToStartPage(userRole);
                }
            } catch (e) {
                router.push({ name: Routes.LOGIN });
            } finally {
                context.dispatch('hideSpinner');
            }
        },

        async saveUserInfo(context, data) {
            const userData = new UserData(data);
            userData.save();
            context.commit(Mutations.SET_USER_DATA, userData);
        },

        async getCurrentUserSchools(context) {
            const { data } = await LoginService.getSchoolsByCurrentUser();
            context.commit(Mutations.SET_CURRENT_USER_SCHOOLS, data);
        },

        switchSchool(context, schoolId) {
            context.dispatch('openConfirmDialog', new Confirmation(async () => {
                context.commit(Mutations.SWITCH_ACCOUNT_REQUEST);
                try {
                    const { data } = await LoginService.switchSchool(schoolId);
                    router.push({ name: Routes.PERSONAL_INFO_PAGE });
                    context.commit(RootMutations.RESET_STATE);
                    context.commit(AbilityMutations.SET_ABILITY, null);
                    context.dispatch('configureAbility', data.userRole);
                    context.dispatch('saveUserInfo', data);
                    setTimeout(() => {
                        context.commit(Mutations.SWITCH_ACCOUNT_SUCCESS);
                    });
                    context.dispatch('getUserData');
                } catch (e) {
                    context.commit(Mutations.SWITCH_ACCOUNT_FAILED)
                }
            }));
        },

        async updateEmail(context, token) {
            try {
                const { data } = await UsersService.updateEmail(token);
                context.dispatch('saveUserInfo', data);
                router.push({ name: Routes.PERSONAL_INFO_PAGE });
            } catch (e) {
                router.push({ name: Routes.LOGIN });
            }
        },

        async changePassword(context, payload) {
            const { schoolId, userId, form } = payload;
            const loginForm = {
                userName: context.getters.userInfo.email,
                password: payload.form.newPassword
            };

            try {
                context.dispatch('showSpinner');
                await UsersService.changePassword(schoolId, userId, form);
                context.dispatch('createSuccessNotification', 'You have successfully changed your password');

                const { data } = await LoginService.login(loginForm);
                context.dispatch('saveUserInfo', data);
                context.dispatch('getSchoolInfo');
            } finally {
                context.dispatch('saveUsername', loginForm.userName);
                context.dispatch('hideSpinner');
            }
        }
    },

    getters: {
        schoolId: state => state.userData && state.userData.schoolId,

        userId: state => state.userData && state.userData.userId,

        userRole: state => state.userData && state.userData.userRole,

        userExists: state => state.userExists,

        currentSchool: state => state.userData && state.schools.find(school =>
            school.id === state.userData.schoolId),

        otherSchools: state => state.userData && state.schools.filter(school =>
            school.id !== state.userData.schoolId),

        isSwitchingAccount: state => state.isSwitchingAccount
    }
};

export function getStartPage(role) {
    return role === Roles.SCHOOL_INSTRUCTOR ?
        Routes.CLASS_PAGE :
        Routes.DASHBOARD
}

function navigateToStartPage(role) {
    const startPage = getStartPage(role);
    router.push({ name: startPage });
}


// WEBPACK FOOTER //
// ./src/store/modules/login/login.module.js